import { createModel } from '@rematch/core';
import moment from 'moment';
import { filterNullish } from '@modernhealth/filter-nullish';

import { appClient } from 'src/config/apollo';
import {
    GetCoachingSessionsDocument,
    GetCoachingSessionsQuery,
    GetCoachingSessionsQueryVariables,
} from 'src/modules/videoSessions/documents/getCoachingSessions.generated';
import { toastNotificationsService } from 'src/config/rootService';
import { RootModel } from 'src/config/rootStore';
import { SessionUtilizationInfo } from 'src/modules/videoSessions/models/videoSessions';
import { FetchStatus } from 'src/shared/models/shared';

export type CoachingVisit = NonNullable<
    NonNullable<GetCoachingSessionsQuery['coachingVisits']>[number]
>;

export interface VideoSessionsState {
    fetchStatus: FetchStatus;
    sessions: CoachingVisit[] | null;
    sessionUtilizationInfo: SessionUtilizationInfo;
}

const defaultState: VideoSessionsState = {
    fetchStatus: FetchStatus.NotStarted,
    sessions: null,
    sessionUtilizationInfo: {
        coachingVisitsRemaining: 0,
        therapyVisitsRemaining: 0,
        resetDate: moment().add('1 year').startOf('year'),
        coveredTherapyVisits: 0,
        coveredCoachingVisits: 0,
    },
};

export const videoSessions = createModel<RootModel>()({
    state: defaultState,
    reducers: {
        addSessions: (state, payload: CoachingVisit[] | null): VideoSessionsState => {
            return {
                ...state,
                sessions: payload,
            };
        },
        addSessionUtilizationInfo: (state, payload: SessionUtilizationInfo): VideoSessionsState => {
            return {
                ...state,
                sessionUtilizationInfo: payload,
            };
        },
        fetchingStarted: (state): VideoSessionsState => {
            return {
                ...state,
                fetchStatus: FetchStatus.InProgress,
            };
        },
        fetchingCompleted: (state): VideoSessionsState => {
            return {
                ...state,
                fetchStatus: FetchStatus.Completed,
            };
        },
        fetchingFailed: (state): VideoSessionsState => {
            return {
                ...state,
                fetchStatus: FetchStatus.Failed,
            };
        },
        'authentication/onLogout': (): VideoSessionsState => {
            return defaultState;
        },
    },
    effects: dispatch => ({
        async fetchSessions(): Promise<void> {
            dispatch.videoSessions.fetchingStarted();

            try {
                const { data } = await appClient.query<
                    GetCoachingSessionsQuery,
                    GetCoachingSessionsQueryVariables
                >({
                    query: GetCoachingSessionsDocument,
                    fetchPolicy: 'network-only',
                });

                const coachingVisits = data.coachingVisits
                    ? filterNullish(data.coachingVisits)
                    : null;
                dispatch.videoSessions.addSessions(coachingVisits);
                dispatch.videoSessions.fetchingCompleted();
            } catch (error) {
                dispatch.videoSessions.fetchingFailed();
                toastNotificationsService.renderErrorToast('Could not fetch coaching sessions.');
            }
        },
    }),
});
